/**
 * Slash command registry — pure data.
 *
 * Each entry:
 *   id          — unique identifier used by the executor
 *   trigger     — the slash command string the user types
 *   label       — short display name shown in the command list
 *   description — one-line description shown in help and command list
 *   roles       — null = all roles; or array of role names that can see this command
 *   args        — brief arg hint shown after the trigger (e.g. '[hours]')
 *   hidden      — true to exclude from the autocomplete suggestion list while typing.
 *                 The command still executes if typed explicitly. Use for commands that
 *                 are self-describing (e.g. /help) or that show up another way.
 *   projectArg  — true if the command's argument is a project name/number.
 *                 CommandBar shows live project autocomplete as the user types
 *                 the argument; Enter navigates without calling the executor.
 *                 Use this for any command that accepts an optional [project] arg:
 *                 /go, /overdue [project], /mine [project], /status [project], etc.
 *
 * To add a new command: add an entry here, then add the executor case in commandExecutor.js.
 */

export const COMMANDS = [
  {
    id:          'home',
    trigger:     '/home',
    label:       'Home',
    description: 'Go to the top-level project list',
    roles:       null,
    args:        '',
  },
  {
    id:          'dark',
    trigger:     '/dark',
    label:       'Dark mode',
    description: 'Switch to dark mode',
    roles:       null,
    args:        '',
  },
  {
    id:          'light',
    trigger:     '/light',
    label:       'Light mode',
    description: 'Switch to light mode',
    roles:       null,
    args:        '',
  },
  {
    id:          'help',
    trigger:     '/help',
    label:       'Help',
    description: 'Show all available commands and what they do',
    roles:       null,
    args:        '',
    hidden:      true,   // the full command list already appears when typing /; no need to list /help explicitly
  },
  {
    id:          'mine',
    trigger:     '/mine',
    label:       'My projects',
    description: 'Show only the projects you are crewed on (your flights stay pinned above the outline)',
    roles:       null,
    args:        '',
  },
  {
    id:          'all',
    trigger:     '/all',
    label:       'All projects',
    description: 'Show every active project in the outline, not just the ones you are crewed on',
    roles:       null,
    args:        '',
  },
  {
    id:          'team',
    trigger:     '/team',
    label:       'Team',
    description: 'Show the crew flying the currently zoomed milestone',
    roles:       null,
    args:        '',
  },
  {
    id:          'go',
    trigger:     '/go',
    label:       'Go to project',
    description: 'Navigate to a project — type a name or number to filter',
    roles:       null,
    args:        '[name or number]',
    projectArg:  true,
  },
  {
    id:          'time',
    trigger:     '/time',
    label:       'Log time',
    description: 'Log hours against the current phase — navigate to a phase or milestone first',
    roles:       null,
    args:        '[hours]',
  },
  {
    id:          'expand',
    trigger:     '/expand',
    label:       'Expand all',
    description: 'Expand all items at the current zoom level (phases, milestones, or projects)',
    roles:       null,
    args:        '',
  },
  {
    id:          'collapse',
    trigger:     '/collapse',
    label:       'Collapse all',
    description: 'Collapse all items at the current zoom level',
    roles:       null,
    args:        '',
  },
  {
    id:          'filter',
    trigger:     '/filter',
    label:       'Filter',
    description: 'Show or hide completed milestones across the outline',
    roles:       null,
    args:        '',
  },
  {
    id:          'permits',
    trigger:     '/permits',
    label:       'Permit triage',
    description: 'Agency reviews where we have the ball, across all projects',
    roles:       null,
    args:        '',
  },
  {
    id:          'drawings',
    trigger:     '/drawings',
    label:       'Drawing Sheets',
    description: 'Jump to Drawing Sheets for the current project',
    roles:       null,
    args:        '',
  },
  {
    id:          'bim',
    trigger:     '/bim',
    label:       'BIM Systems',
    description: 'Jump to BIM Systems for the current project',
    roles:       null,
    args:        '',
  },
  {
    id:          'sketch',
    trigger:     '/sketch',
    label:       'AlderSKETCH',
    description: 'Open AlderSKETCH — the trace and sketch tool',
    roles:       null,
    args:        '',
  },
  {
    id:          'cad',
    trigger:     '/cad',
    label:       'AlderCAD sandbox',
    description: 'Open a project-less AlderCAD test model in a new tab — /cad <name> for a separate one',
    roles:       null,
    args:        '[name]',
  },
  {
    id:          'refresh',
    trigger:     '/refresh',
    label:       'Refresh data',
    description: 'Reload data for the current view — use when something looks stale instead of reloading the page',
    roles:       null,
    args:        '',
  },
  {
    id:          'split',
    trigger:     '/split',
    label:       'Split screen',
    description: 'Open a side-by-side replica of the current screen — same as Alt+click on a node, but from anywhere',
    roles:       null,
    args:        '',
  },
  {
    id:          'debug',
    trigger:     '/debug',
    label:       'Debug node IDs',
    description: 'Toggle node ID badges over each bullet so you can reference a node by its key (managers and up)',
    // NOT gated here — CommandBar's `roles` filter checks the Airtable Member Type field
    // ('Team - Principal', etc.), a different system from the Supabase seat tiers ('manager',
    // 'admin') this command is actually restricted to. Setting roles here would just make the
    // command invisible to everyone (no Member Type ever equals 'manager'/'admin'). The real gate
    // is commandExecutor.js's execDebug(), which checks debugOverlay.isEligible() live against
    // the Supabase seat and returns a clear message for anyone below manager.
    roles:       null,
    args:        '',
  },
  {
    id:          'aj',
    trigger:     '/aj',
    label:       'AJ',
    description: 'AJ the Coastal Fox takes a stroll',
    roles:       null,
    args:        '',
    hidden:      true,   // secret — excluded from autocomplete AND from /help; runs only if typed exactly
  },
]
